/**
 * One runner for every availability write in the showings setup: tracks the saving state, shows one
 * inline error, and refreshes both showings queries after a successful write.
 */

import { useCallback, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';

export interface AvailabilityActionRunner {
  /** Runs the write; resolves true when it succeeded. Failures set `error` instead of throwing. */
  run: (action: () => Promise<unknown>, failureMessage: string) => Promise<boolean>;
  saving: boolean;
  error: string | null;
  clearError: () => void;
}

export function useAvailabilityAction(listingId: string): AvailabilityActionRunner {
  const queryClient = useQueryClient();
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const run = useCallback(
    async (action: () => Promise<unknown>, failureMessage: string): Promise<boolean> => {
      setSaving(true);
      setError(null);
      try {
        await action();
        return true;
      } catch (err) {
        setError(err instanceof Error && err.message ? err.message : failureMessage);
        return false;
      } finally {
        // Refresh even after a failure: a multi-step change may have partly applied.
        await queryClient.invalidateQueries({ queryKey: ['listing', listingId, 'availability'] });
        await queryClient.invalidateQueries({ queryKey: ['listing', listingId, 'showings'] });
        setSaving(false);
      }
    },
    [listingId, queryClient]
  );

  return { run, saving, error, clearError: () => setError(null) };
}
